import {
  ArrowLeftRight,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  Settings,
  ShoppingBag,
  User,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { TierBadge } from "@/components/ui/tier-badge";
import {
  getBookingStatusInfo,
  getOutcomeStatusInfo,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";
import {
  BookingStatusSelect,
  OutcomeSelect,
} from "@/domains/appointment/appointments/shared/utils/dropdown-builders";
import { getDisplayPhone } from "@/domains/appointment/appointments/utils/appointment-phone-utils";
import {
  formatTime,
  getCustomerName,
  getDuration,
} from "@/domains/appointment/appointments/utils/appointment-utils";
import { CustomerDetailDialog } from "@/domains/customer/customers/components/dialogs";
import { useConfig } from "@/hooks/use-config";
import { useLoyaltyProgram } from "@/hooks/use-loyalty-program";
import { useCurrentAbility } from "@/lib/authorization";
import { useSalonStore } from "@/lib/store/salon";
import { formatPhoneNumber } from "@/lib/utils/phone-formatter";
import { useAppointmentDetailView } from "../use-appointment-detail-view";
import { DeclineAppointmentDialog } from "./decline-appointment-dialog";
import { EditCancellationDialog } from "./edit-cancellation-dialog";
import { EditCustomerDialog } from "./edit-customer-dialog";
import { EditGuestCancellationDialog } from "./edit-guest-cancellation-dialog";
import { EditServicesDialog } from "./edit-services-dialog";
import { EditTimeDialog } from "./edit-time-dialog";
import { ReassignCustomerDialog } from "./reassign-customer-dialog";
import { SetServicePriceDialog } from "./set-service-price-dialog";

interface MobileAppointmentDetailProps {
  appointmentId: string;
}

export function MobileAppointmentDetail({ appointmentId }: MobileAppointmentDetailProps) {
  const { t } = useTranslation("appointments");
  const [showMessages, setShowMessages] = useState(false);
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [showReassignCustomer, setShowReassignCustomer] = useState(false);
  const [showEditTime, setShowEditTime] = useState(false);
  const ability = useCurrentAbility();
  const canUpdate = ability.can("update", "Appointment");
  const { currentSalon } = useSalonStore();

  // Get configuration with real-time updates
  const { value: showWorkerAssignment = false } = useConfig(
    "salon.appointment_worker_assignment_enabled"
  );
  const { value: showOutcomeTracking = true } = useConfig("salon.outcome_tracking_enabled");
  const showLoyaltyTiers = useLoyaltyProgram();

  const {
    currentAppointment,
    customerData,
    workers,
    localNotes,
    appointmentLoading,
    isUpdating,
    showCustomerDetail,
    setShowCustomerDetail,
    showDeclineDialog,
    setShowDeclineDialog,
    showEditCancellationDialog,
    setShowEditCancellationDialog,
    showEditGuestCancellationDialog,
    setShowEditGuestCancellationDialog,
    handleStatusChange,
    handlePaymentMethodChange,
    handleDeclineConfirm,
    handleEditCancellationConfirm,
    handleEditGuestCancellationConfirm,
    handleServiceWorkerChange,
    handleNotesChange,
  } = useAppointmentDetailView({ appointmentId });
  const [showEditServices, setShowEditServices] = useState(false);
  const [showSetPriceDialog, setShowSetPriceDialog] = useState(false);

  // Get phone to display: prioritize bookingPhone, fallback to customer's phone numbers
  const appointmentCustomer = currentAppointment?.customer ?? null;
  const displayPhone = getDisplayPhone(
    currentAppointment?.bookingPhone,
    appointmentCustomer?.phoneNumbers
  );

  // Show loading state while fetching
  if (appointmentLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  // Don't render if no appointment data
  if (!currentAppointment) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Time & Duration Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between pl-1">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              {t("details.mobile.timeDuration")}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setShowEditTime(true)}
          >
            <Pencil className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="font-semibold text-lg">{formatTime(currentAppointment.startTime)}</div>
          <div className="text-sm text-muted-foreground mt-1">
            {getDuration(currentAppointment)} {t("details.labels.minutes")}
          </div>
        </div>
      </div>

      {/* Customer Message Section */}
      {currentAppointment.customerMessage && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 pl-1">
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              {t("details.labels.customerMessage")}
            </span>
          </div>

          <div className="p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="text-sm text-yellow-900 dark:text-yellow-100">
              {currentAppointment.customerMessage}
            </div>
          </div>
        </div>
      )}

      {/* Guest Information Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between pl-1">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              {t("details.mobile.guestInformation")}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <Pencil className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowEditCustomer(true)}>
                <Pencil className="w-4 h-4 mr-2" />
                {t("details.actions.editCustomer")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowReassignCustomer(true)}>
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                {t("details.actions.reassignCustomer")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Contact Info Card */}
        <div className="p-3 bg-muted/30 rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-muted-foreground" />
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-primary font-medium">
                    {getCustomerName(currentAppointment)}
                  </span>
                  {currentAppointment.customer?.totalVisits === 0 && (
                    <Badge
                      variant="secondary"
                      className="text-xs px-1.5 py-0 h-5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                    >
                      {t("common.newCustomer")}
                    </Badge>
                  )}
                </div>
                {showLoyaltyTiers &&
                  currentAppointment.customer?.currentTier &&
                  currentAppointment.customer.currentTier.name && (
                    <div className="mt-1">
                      <TierBadge
                        tier={currentAppointment.customer.currentTier}
                        size="sm"
                        showPriorityIcon={true}
                      />
                    </div>
                  )}
              </div>
            </div>
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => setShowCustomerDetail(true)}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {displayPhone && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span className="text-primary">{formatPhoneNumber(displayPhone.phone)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0"
                  onClick={() => window.open(`tel:${displayPhone.phone}`)}
                >
                  <Phone className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0"
                  onClick={() => window.open(`sms:${displayPhone.phone}`)}
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          {currentAppointment.customer?.email && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-primary">{currentAppointment.customer.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => window.open(`mailto:${currentAppointment.customer?.email}`)}
              >
                <Mail className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Traffic Source */}
          {currentAppointment.trafficSource && (
            <div className="space-y-1 pt-2 border-t">
              <Label className="text-xs font-medium text-muted-foreground">Traffic Source</Label>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-muted-foreground" />
                <div className="font-medium text-sm">{currentAppointment.trafficSource.name}</div>
              </div>
              {currentAppointment.trafficSource.description && (
                <div className="text-xs text-muted-foreground pl-6">
                  {currentAppointment.trafficSource.description}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Services Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between pl-1">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              {(currentAppointment.appointmentServices?.length ?? 0) > 1
                ? t("details.sections.services")
                : t("details.sections.service")}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setShowEditServices(true)}
          >
            <Pencil className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg space-y-3">
          <div className="space-y-4">
            {currentAppointment.appointmentServices
              ?.slice()
              .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
              .map((service) => {
                const price =
                  service.pricingMode === "NO_PRICE"
                    ? service.totalPrice
                    : service.totalPrice || service.basePrice;
                return (
                  <div key={service.id} className="space-y-2">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-sm mb-1">{service.serviceName}</div>
                        <div className="text-xs text-muted-foreground">
                          {service.serviceDuration} {t("details.labels.minutes")}
                        </div>
                        {service.pricingMode === "NO_PRICE" && price === 0 && (
                          <div className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            {t("details.labels.quoteRequired")}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {canUpdate && service.pricingMode === "NO_PRICE" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => setShowSetPriceDialog(true)}
                          >
                            {t("details.actions.setPrice")}
                          </Button>
                        )}
                        {service.pricingMode === "NO_PRICE" && price > 0 && (
                          <div className="text-sm font-semibold text-primary">
                            <PriceDisplay amount={price} />
                          </div>
                        )}
                      </div>
                    </div>
                    {service.pricingMode !== "NO_PRICE" && (
                      <div className="text-right">
                        {service.discountAmount && service.discountAmount > 0 ? (
                          <div className="space-y-0.5">
                            <div className="text-xs text-muted-foreground line-through">
                              <PriceDisplay amount={service.originalPrice || service.basePrice} />
                            </div>
                            <div className="text-sm font-semibold text-green-600">
                              <PriceDisplay amount={service.totalPrice || service.basePrice} />
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm font-semibold text-primary">
                            <PriceDisplay amount={service.totalPrice || service.basePrice} />
                          </div>
                        )}
                      </div>
                    )}

                    {service.pricingMode === "MATRIX_PRICE" && (
                      <div className="text-xs space-y-1 pl-3 border-l-2 border-muted bg-muted/30 py-2 rounded-r">
                        {(service.basePrice ?? 0) > 0 && (
                          <div className="flex justify-between">
                            <span>{t("details.labels.basePrice")}:</span>
                            <span>
                              <PriceDisplay amount={service.basePrice} />
                            </span>
                          </div>
                        )}
                        {service.selectedOptions && service.selectedOptions.length > 0 && (
                          <div>
                            <span className="font-medium">
                              {t("details.labels.configuration")}:
                            </span>{" "}
                            {service.selectedOptions.map((o) => o.optionName).join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Total Price */}
          {currentAppointment.totalPrice && currentAppointment.totalPrice > 0
            ? (currentAppointment.appointmentServices?.length || 0) > 1 && (
                <div className="flex justify-between items-center pt-3 border-t">
                  <span className="font-semibold text-base">{t("details.labels.totalPrice")}</span>
                  <span className="text-xl font-bold text-primary">
                    <PriceDisplay amount={currentAppointment.totalPrice} />
                  </span>
                </div>
              )
            : null}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Management Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pl-1">
          <Settings className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">
            {t("details.mobile.management")}
          </span>
        </div>

        {/* Service Workers */}
        {showWorkerAssignment && (
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">
              {t("details.labels.serviceWorkers")}
            </Label>
            <div className="space-y-2">
              {currentAppointment.appointmentServices
                ?.slice()
                .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                .map((service) => {
                  const workerName = service.worker
                    ? service.worker.displayName ||
                      (service.worker.user
                        ? `${service.worker.user.firstName} ${service.worker.user.lastName}`
                        : "Unknown Worker")
                    : t("details.labels.unassigned");

                  return (
                    <div key={service.id} className="bg-muted/20 rounded p-2 space-y-2">
                      <div className="text-xs font-medium">{service.serviceName}</div>

                      {canUpdate ? (
                        <Select
                          value={service.workerId || "unassigned"}
                          onValueChange={(workerId) => {
                            handleServiceWorkerChange(
                              service.id,
                              workerId === "unassigned" ? null : workerId
                            );
                          }}
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue>
                              {service.worker ? (
                                <span className="text-xs">{workerName}</span>
                              ) : (
                                <span className="text-xs">{t("details.labels.unassigned")}</span>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">
                              {t("details.labels.unassigned")}
                            </SelectItem>
                            {workers.map((worker) => {
                              // Only offer workers enrolled in the current salon
                              const workerSalonId = worker.workerSalons?.find(
                                (ws) => ws.salonId === currentSalon?.id
                              )?.id;

                              if (!workerSalonId) return null;

                              return (
                                <SelectItem key={worker.id} value={worker.id}>
                                  <span className="text-sm">
                                    {worker.displayName ||
                                      (worker.user
                                        ? `${worker.user.firstName} ${worker.user.lastName}`
                                        : "Unknown Worker")}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="h-8 px-2 py-1 bg-muted/50 border border-border rounded-md flex items-center text-xs">
                          {service.worker ? workerName : t("details.labels.unassigned")}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Booking Status and Outcome */}
        <div className={`grid gap-3 ${showOutcomeTracking ? "grid-cols-2" : "grid-cols-1"}`}>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">
              {t("details.mobile.booking")}
            </Label>
            {canUpdate ? (
              <BookingStatusSelect
                value={currentAppointment.bookingStatus}
                onValueChange={(value) => handleStatusChange("bookingStatus", value)}
                disabled={isUpdating}
                className="h-9 text-sm"
                placeholder={t("details.mobile.status")}
              />
            ) : (
              (() => {
                const statusInfo = getBookingStatusInfo(currentAppointment.bookingStatus);
                const IconComponent = statusInfo.icon;
                return (
                  <div className="h-9 px-3 py-2 bg-muted/50 border border-border rounded-md flex items-center gap-2 text-sm">
                    <IconComponent className={`w-4 h-4 ${statusInfo.iconColor}`} />
                    {statusInfo.text}
                  </div>
                );
              })()
            )}
          </div>

          {showOutcomeTracking && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">
                {t("details.labels.outcome")}
              </Label>
              {canUpdate ? (
                <OutcomeSelect
                  value={currentAppointment.outcome ?? null}
                  onValueChange={(value) => handleStatusChange("outcome", value)}
                  disabled={isUpdating}
                  className="h-9 text-sm"
                  placeholder={t("details.mobile.outcome")}
                />
              ) : (
                (() => {
                  if (!currentAppointment.outcome) {
                    return (
                      <div className="h-9 px-3 py-2 bg-muted/50 border border-border rounded-md flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full bg-gray-400" />
                        {t("details.labels.notStarted")}
                      </div>
                    );
                  }
                  const outcomeInfo = getOutcomeStatusInfo(currentAppointment.outcome);
                  const IconComponent = outcomeInfo.icon;
                  return (
                    <div className="h-9 px-3 py-2 bg-muted/50 border border-border rounded-md flex items-center gap-2 text-sm">
                      <IconComponent className={`w-3 h-3 ${outcomeInfo.iconColor}`} />
                      {outcomeInfo.text}
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>

        {/* Payment Method */}
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground">{t("paymentMethod")}</Label>
          {canUpdate ? (
            <Select
              value={currentAppointment.paymentMethod ?? "none"}
              onValueChange={(value) => handlePaymentMethodChange(value === "none" ? null : value)}
              disabled={isUpdating}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue>
                  {t(`paymentMethod_${currentAppointment.paymentMethod ?? "NONE"}`)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("paymentMethod_NONE")}</SelectItem>
                <SelectItem value="CASH">{t("paymentMethod_CASH")}</SelectItem>
                <SelectItem value="BANKING">{t("paymentMethod_BANKING")}</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="h-9 px-3 py-2 bg-muted/50 border border-border rounded-md flex items-center text-sm">
              {currentAppointment.paymentMethod
                ? t(`paymentMethod_${currentAppointment.paymentMethod}`)
                : "—"}
            </div>
          )}
        </div>

        {/* Owner Cancellation Reason */}
        {currentAppointment.cancellationReasonType && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-muted-foreground">
                {t("details.labels.ownerCancellationReason")}
              </Label>
              {canUpdate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setShowEditCancellationDialog(true)}
                >
                  <Pencil className="w-3 h-3" />
                </Button>
              )}
            </div>
            <div className="p-2 bg-muted/30 rounded-lg text-sm">
              <div className="font-medium">
                {t(`decline.reasons.${currentAppointment.cancellationReasonType}`)}
              </div>
              {currentAppointment.cancellationReasonText && (
                <div className="mt-1 text-muted-foreground text-xs">
                  {currentAppointment.cancellationReasonText}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Guest Cancellation Reason */}
        {currentAppointment.guestCancellationReason && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-muted-foreground">
                {t("details.labels.guestCancellationReason")}
              </Label>
              {canUpdate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setShowEditGuestCancellationDialog(true)}
                >
                  <Pencil className="w-3 h-3" />
                </Button>
              )}
            </div>
            <div className="p-2 bg-muted/30 rounded-lg text-sm">
              <div className="font-medium">
                {t(`cancellationReasons.${currentAppointment.guestCancellationReason}`)}
              </div>
              {currentAppointment.guestCancellationText && (
                <div className="mt-1 text-muted-foreground text-xs">
                  {currentAppointment.guestCancellationText}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground">
            {t("details.sections.notes")}
          </Label>
          {canUpdate ? (
            <Textarea
              value={localNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder={t("details.mobile.addNotes")}
              className="min-h-[80px] text-sm resize-none"
              disabled={isUpdating}
            />
          ) : (
            <div className="min-h-[80px] px-3 py-2 bg-muted/50 border border-border rounded-md text-sm">
              {currentAppointment.notes || t("details.labels.noNotes")}
            </div>
          )}
        </div>

        {/* Internal Notes */}
        {currentAppointment.internalNotes && (
          <Collapsible open={showMessages} onOpenChange={setShowMessages}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full p-0 h-auto justify-between">
                <span className="font-medium text-sm">{t("details.labels.systemNotes")}</span>
                {showMessages ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3">
              <div className="border-t pt-3">
                <div className="text-sm">{currentAppointment.internalNotes}</div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      {/* Customer Detail Dialog */}
      {showCustomerDetail && customerData?.customer && (
        <CustomerDetailDialog
          customer={customerData.customer}
          onClose={() => setShowCustomerDetail(false)}
        />
      )}

      {/* Decline Appointment Dialog */}
      <DeclineAppointmentDialog
        open={showDeclineDialog}
        onClose={() => setShowDeclineDialog(false)}
        onConfirm={handleDeclineConfirm}
        isLoading={isUpdating}
      />

      {/* Edit Owner Cancellation Dialog */}
      {currentAppointment?.cancellationReasonType && (
        <EditCancellationDialog
          open={showEditCancellationDialog}
          onClose={() => setShowEditCancellationDialog(false)}
          onConfirm={handleEditCancellationConfirm}
          initialReasonType={currentAppointment.cancellationReasonType as any}
          initialReasonText={currentAppointment.cancellationReasonText || ""}
          isLoading={isUpdating}
        />
      )}

      {/* Edit Guest Cancellation Dialog */}
      {currentAppointment?.guestCancellationReason && (
        <EditGuestCancellationDialog
          open={showEditGuestCancellationDialog}
          onClose={() => setShowEditGuestCancellationDialog(false)}
          onConfirm={handleEditGuestCancellationConfirm}
          initialReasonType={currentAppointment.guestCancellationReason as any}
          initialReasonText={currentAppointment.guestCancellationText || ""}
          isLoading={isUpdating}
        />
      )}

      {/* Edit Customer Dialog */}
      {currentAppointment && appointmentCustomer && (
        <EditCustomerDialog
          open={showEditCustomer}
          onOpenChange={setShowEditCustomer}
          appointment={currentAppointment}
          customer={appointmentCustomer}
        />
      )}

      {/* Reassign Customer Dialog */}
      {currentAppointment && (
        <ReassignCustomerDialog
          open={showReassignCustomer}
          onOpenChange={setShowReassignCustomer}
          appointment={currentAppointment}
        />
      )}

      {/* Edit Time Dialog (mobile) */}
      {currentAppointment && (
        <EditTimeDialog
          open={showEditTime}
          onOpenChange={setShowEditTime}
          appointmentId={currentAppointment.id}
          currentStartTime={currentAppointment.startTime}
          currentDuration={getDuration(currentAppointment)}
        />
      )}

      {/* Edit Services Dialog (mobile) */}
      {currentAppointment && (
        <EditServicesDialog
          open={showEditServices}
          onOpenChange={setShowEditServices}
          appointmentId={currentAppointment.id}
          currentStartTime={currentAppointment.startTime}
          appointmentServices={currentAppointment.appointmentServices}
        />
      )}

      {/* Set Service Price Dialog (mobile) */}
      {currentAppointment && (
        <SetServicePriceDialog
          open={showSetPriceDialog}
          onOpenChange={setShowSetPriceDialog}
          appointmentId={currentAppointment.id}
          noPriceServices={
            currentAppointment.appointmentServices
              ?.filter((s) => s.pricingMode === "NO_PRICE")
              .map((s) => ({
                id: s.id,
                serviceName: s.serviceName,
                previousPrice: s.totalPrice || s.basePrice || undefined,
              })) ?? []
          }
        />
      )}
    </div>
  );
}
